import { useMemo } from 'react';

import { useBoolean } from 'src/hooks/use-boolean';

import { PaymentState, SubscriptionContextType } from './subscription-context';
import { derivePaymentState } from './payment-policy';
import { useSubscriptionStatus } from './use-subscription-status';
import { useTariffData } from './use-tariff-data';

// ----------------------------------------------------------------------

function calcUsagePct(current?: number, limit?: number | null): number {
  if (limit == null || limit === 0) return 0;
  return ((current ?? 0) / limit) * 100;
}

// ----------------------------------------------------------------------

export const useSubscriptionData = (): SubscriptionContextType => {
  const { data: status, isLoading, isBlockedByApi } = useSubscriptionStatus();

  const tariffData = useTariffData(status);
  const { tariffLimits } = tariffData;

  const { value: paymentBannerDismissed, onTrue: dismissPaymentBanner } = useBoolean();
  const { value: studentBannerDismissed, onTrue: dismissStudentBanner } = useBoolean();
  const { value: memberBannerDismissed, onTrue: dismissMemberBanner } = useBoolean();

  const paymentState = useMemo<PaymentState>(() => {
    if (isBlockedByApi) return 'blocked';
    if (!status) return 'idle';
    return derivePaymentState(status.days_overdue, status.due_date);
  }, [status, isBlockedByApi]);

  const studentUsed = tariffLimits.students?.current ?? 0;
  const studentLimit = tariffLimits.students?.limit ?? 0;
  const memberUsed = tariffLimits.members?.current ?? 0;
  const memberLimit = tariffLimits.members?.limit ?? 0;

  const studentUsagePct = useMemo(
    () => calcUsagePct(studentUsed, studentLimit),
    [studentUsed, studentLimit]
  );

  const memberUsagePct = useMemo(
    () => calcUsagePct(memberUsed, memberLimit),
    [memberUsed, memberLimit]
  );

  const isMemberAtLimit = useMemo(() => memberUsagePct >= 100, [memberUsagePct]);

  const isStudentAtLimit = useMemo(() => studentUsagePct >= 100, [studentUsagePct]);

  const showMemberBanner = useMemo(
    () => memberUsagePct >= 80 && !memberBannerDismissed,
    [memberUsagePct, memberBannerDismissed]
  );

  const showStudentBanner = useMemo(
    () => studentUsagePct >= 80 && !studentBannerDismissed,
    [studentUsagePct, studentBannerDismissed]
  );

  return useMemo<SubscriptionContextType>(
    () => ({
      ...tariffData,
      status: status ?? null,
      isLoading,
      paymentState,
      studentUsagePct,
      studentUsed,
      studentLimit,
      isStudentAtLimit,
      showStudentBanner,
      memberUsagePct,
      memberUsed,
      memberLimit,
      isMemberAtLimit,
      showMemberBanner,
      paymentBannerDismissed,
      dismissPaymentBanner,
      studentBannerDismissed,
      dismissStudentBanner,
      memberBannerDismissed,
      dismissMemberBanner,
    }),
    [
      tariffData,
      status,
      isLoading,
      paymentState,
      studentUsagePct,
      studentUsed,
      studentLimit,
      isStudentAtLimit,
      showStudentBanner,
      memberUsagePct,
      memberUsed,
      memberLimit,
      isMemberAtLimit,
      showMemberBanner,
      paymentBannerDismissed,
      dismissPaymentBanner,
      studentBannerDismissed,
      dismissStudentBanner,
      memberBannerDismissed,
      dismissMemberBanner,
    ]
  );
};
